import moment from 'moment'

/**
 * Cookie helpers — primarily for reading/refreshing Sanctum's
 * `XSRF-TOKEN` cookie (read by axios via `withXSRFToken: true`).
 *
 * `path` defaults to `/` so the cookie is global; flip to a subpath
 * only when intentional.
 *
 * `secure: true` + `SameSite=Lax` defaults match Sanctum's stateful
 * cookie configuration. Don't lower these casually.
 */
export const useCookie = () => {
  const setCookie = (
    name: string,
    value: string,
    days?: number,
    path = '/',
    secure = true,
    sameSite: 'Strict' | 'Lax' | 'None' = 'Lax',
  ): void => {
    let expires = ''
    if (typeof days === 'number') {
      const date = moment().add(days, 'days').toDate()
      expires = `; expires=${date.toUTCString()}`
    }
    const secureFlag = secure ? '; Secure' : ''
    document.cookie =
      `${encodeURIComponent(name)}=${encodeURIComponent(value)}${expires}` +
      `; path=${path}; SameSite=${sameSite}${secureFlag}`
  }

  const getCookie = (name: string): string | undefined => {
    const target = `${encodeURIComponent(name)}=`
    const decoded = document.cookie
      .split(';')
      .map((c) => c.trim())
      .find((c) => c.startsWith(target))
    return decoded ? decodeURIComponent(decoded.slice(target.length)) : undefined
  }

  const hasCookie = (name: string): boolean => getCookie(name) !== undefined

  /**
   * A cookie is "valid" if it exists. Browsers strip expired
   * cookies automatically — if `getCookie` returns a value, the
   * browser still considers it live.
   */
  const isCookieValid = (name: string): boolean => hasCookie(name)

  /**
   * Delete a cookie. Pass `domain` when the cookie was set with an
   * explicit `Domain` attribute (e.g., Sanctum's `XSRF-TOKEN` lands
   * with `domain=.${HOST}` — must be deleted with the matching
   * domain or the browser keeps it).
   *
   * Common pattern when sweeping potentially-stale auth cookies:
   * call once with the explicit domain, once without — covers both
   * domain-scoped and host-only-scoped variants.
   */
  const deleteCookie = (name: string, path = '/', domain?: string): void => {
    const encoded = encodeURIComponent(name)
    const domainPart = domain !== undefined ? `; domain=${domain}` : ''
    document.cookie = `${encoded}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}${domainPart}`
  }

  return { setCookie, getCookie, hasCookie, isCookieValid, deleteCookie }
}
